import React, { useState } from "react";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Button, Grid, IconButton } from "@mui/material";
import ErrorHelper from "../../../../../../components/ErrorWrapper";
import Input from "../../../../../../components/Input";
import Text from "../../../../../../components/Text";
import SignatureInput from "../../../../../../components/signiture/SignatureInput";
import { useSignatureController } from "../../../../../../components/signiture/use-signature-controller";

interface ConsentForServicesFormProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
}

const ConsentForServicesForm = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched: _touched,
  errors: _errors,
  viewOnly,
}: ConsentForServicesFormProps) => {
  const [_k, setK] = useState<boolean>(true);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    _k,
    values?.consentForServices?.sign
  );

  return (
    <Box>
      <Text variant="subtitle1" fontWeight="500" mb={1}>
        Preview: Consent for Services
      </Text>

      {/* Scrollable Document Body Container */}
      <Box
        sx={{
          border: "1px solid #c0c0c0",
          borderRadius: "4px",
          p: 2,
          mb: 3,
          backgroundColor: "#ffffff",
          fontFamily: "sans-serif",
          fontSize: "0.9rem",
          lineHeight: "1.5",
          color: "#333333",
        }}
      >
        <p>
          This form is called a Consent for Services (the "Consent"). Your
          therapist, counselor, psychologist, doctor, or other health
          professional ("Provider") has asked you to read and sign this Consent
          before you start therapy. Please review the information. If you have
          any questions, contact your Provider.
        </p>

        <strong>THE THERAPY PROCESS</strong>
        <p>
          Therapy is a collaborative process where you and your Provider will
          work together on equal footing to achieve goals that you define. This
          means that you will follow a defined process supported by scientific
          evidence, where you and your Provider have specific rights and
          responsibilities. Therapy generally shows positive outcomes for
          individuals who follow the process. Better outcomes are often
          associated with a good relationship between a client and their
          Provider. To foster the best possible relationship, it is important
          you understand as much about the process before deciding to commit.
        </p>
        <p>
          Therapy begins with the intake process. First, you will review your
          Provider's policies and procedures, talk about fees, identify
          emergency contacts, and decide if you want health insurance to pay
          your fees depending on your plan's benefits. Second, you will discuss
          what to expect during therapy, including the type of therapy, the
          length of treatment, and the risks and benefits. If your Provider is
          practicing under the supervision of another professional, your
          Provider will tell you about their supervision and the name of the
          supervising professional. Third, you will form a treatment plan,
          including the type of therapy, how often you will attend therapy, your
          short- and long-term goals, and the steps you will take to achieve
          them. Over time, you and your Provider may edit your treatment plan to
          be sure it describes your goals and steps you need to take. After
          intake, you will attend regular therapy sessions at your Provider's
          office or through video, called telehealth. Participation in therapy
          is voluntary - you can stop at any time. At some point, you will
          achieve your goals. At this time, you will review your progress,
          identify supports that will help you maintain your progress, and
          discuss how to return to therapy if you need it in the future.
        </p>

        <strong>IN-PERSON VISITS &amp; SARS-CoV-2 ("COVID-19")</strong>
        <p>
          When guidance from public health authorities allows and your Provider
          offers, you can meet in-person. If you attend therapy in-person, you
          understand:
        </p>
        <ul>
          <li>
            You can only attend if you are symptom-free (For symptoms, see:
            https://www.cdc.gov/coronavirus/2019-ncov/symptoms-testing/symptoms.html);
          </li>
          <li>
            If you are experiencing symptoms, you can switch to a telehealth
            appointment or cancel. If you need to cancel, you will not be
            charged a late cancellation fee.
          </li>
          <li>
            You must follow all safety protocols established by the practice,
            including:
          </li>
          <ul>
            <li>Following the check-in procedure;</li>
            <li>
              Washing or sanitizing your hands upon entering the practice;
            </li>
            <li>Adhering to appropriate social distancing measures;</li>
            <li>Wearing a mask, if required;</li>
            <li>
              Telling your Provider if you have a high risk of exposure to
              COVID-19, such as through school, work, or commuting; and
            </li>
            <li>
              Telling your Provider if you or someone in your home tests
              positive for COVID-19.
            </li>
          </ul>
          <li>
            Your Provider may be mandated to report to public health authorities
            if you have been in the office and have tested positive for
            infection. If so, your Provider may make the report without your
            permission, but will only share necessary information. Your Provider
            will never share details about your visit. Because the COVID-19
            pandemic is ongoing, your ability to meet in person could change
            with minimal or no notice. By signing this Consent, you understand
            that you could be exposed to COVID-19 if you attend in-person
            sessions. If a member of the practice tests positive for COVID-19,
            you will be notified. If you have any questions, or if you want a
            copy of this policy, please ask.
          </li>
        </ul>

        <strong>TELEHEALTH SERVICES</strong>
        <p>
          To use telehealth, you need an internet connection and a device with a
          camera for video. Your Provider can explain how to log in and use any
          features on the telehealth platform. If telehealth is not a good fit
          for you, your Provider will recommend a different option. There are
          some risks and benefits to using telehealth:
        </p>
        <ul>
          <li>
            <strong>Risks</strong>
          </li>
          <ul>
            <li>
              Privacy and Confidentiality. You may be asked to share personal
              information with the telehealth platform to create an account,
              such as your name, date of birth, location, and contact
              information. Your Provider carefully vets any telehealth platform
              to ensure your information is secured to the appropriate
              standards.
            </li>
            <li>
              Technology. At times, you could have problems with your internet,
              video, or sound. If you have issues during a session, your
              Provider will follow the backup plan that you agree to prior to
              sessions.
            </li>
            <li>
              Crisis Management. It may be difficult for your Provider to
              provide immediate support during an emergency or crisis. You and
              your Provider will develop a plan for emergencies or crises, such
              as choosing a local emergency contact, creating a communication
              plan, and making a list of local support, emergency, and crisis
              services.
            </li>
          </ul>
          <li>
            <strong>Benefits</strong>
          </li>
          <ul>
            <li>
              Flexibility. You can attend therapy wherever is convenient for
              you.
            </li>
            <li>
              Ease of Access. You can attend telehealth sessions without
              worrying about traveling, meaning you can schedule less time per
              session and can attend therapy during inclement weather or
              illness.
            </li>
          </ul>
          <li>
            <strong>Recommendations</strong>
          </li>
          <ul>
            <li>
              Make sure that other people cannot hear your conversation or see
              your screen during sessions.
            </li>
            <li>
              Do not use video or audio to record your session unless you ask
              your Provider for their permission in advance.
            </li>
            <li>
              Make sure to let your Provider know if you are not in your usual
              location before starting any telehealth session.
            </li>
          </ul>
        </ul>

        <strong>CONFIDENTIALITY</strong>
        <ul>
          <li>
            If your Provider has reason to believe a minor or elderly individual
            is a victim of abuse or neglect, they are required by law to contact
            the appropriate authorities.
          </li>
          <li>
            If your Provider believes that you are at imminent risk of harming
            yourself, they may contact law enforcement or other crisis services.
            However, before contacting emergency or crisis services, your
            Provider will work with you to discuss other options to keep you
            safe.
          </li>
        </ul>

        <strong>RECORD KEEPING</strong>
        <p>
          Your Provider is required to keep records about your treatment. These
          records help ensure the quality and continuity of your care, as well
          as provide evidence that the services you receive meet the appropriate
          standards of care. Your records are maintained in an electronic health
          record provided by TherapyNotes. TherapyNotes has several safety
          features to protect your personal information, including advanced
          encryption techniques to make your personal information difficult to
          decode, firewalls to prevent unauthorized access, and a team of
          professionals monitoring the system for suspicious activity.
          TherapyNotes keeps records of all log-ins and actions within the
          system.
        </p>

        <strong>COMMUNICATION</strong>
        <p>
          You decide how to communicate with your Provider outside of your
          sessions. You have several options:
        </p>
        <ul>
          <li>
            <strong>Texting/Email</strong>
          </li>
          <ul>
            <li>
              Texting and email are not secure methods of communication and
              should not be used to communicate personal information. You may
              choose to receive appointment reminders via text message or email.
              You should carefully consider who may have access to your text
              messages or emails before choosing to communicate via either
              method.
            </li>
          </ul>
          <li>
            <strong>Secure Communication</strong>
          </li>
          <ul>
            <li>
              Secure communications are the best way to communicate personal
              information, though no method is entirely without risk. Your
              Provider will discuss options available to you. If you decide to
              be contacted via non-secure methods, your Provider will document
              this in your record.
            </li>
          </ul>
          <li>
            <strong>Social Media/Review Websites</strong>
          </li>
          <ul>
            <li>
              If you try to communicate with your Provider via these methods,
              they will not respond. This includes any form of friend or contact
              request, @mention, direct message, wall post, and so on. This is
              to protect your confidentiality and ensure appropriate boundaries
              in therapy.
            </li>
            <li>
              Your provider may publish content on various social media websites
              or blogs. There is no expectation that you will follow, comment
              on, or otherwise engage with any content. If you do choose to
              follow your Provider on any platform, they will not follow you
              back.
            </li>
            <li>
              If you see your Provider on any form of review website, it is not
              a solicitation for a review. Many such sites scrape business
              listings and may automatically include your Provider. If you
              choose to leave a review of your Provider on any website, they
              will not respond. While you are always free to express yourself in
              the manner you choose, please be aware of the potential impact on
              your confidentiality prior to leaving a review. It is often
              impossible to remove reviews later, and some sites aggregate
              reviews from several platforms leading to your review appearing
              other places without your knowledge.
            </li>
          </ul>
        </ul>

        <strong>FEES AND PAYMENT FOR SERVICES</strong>
        <p>
          You may be required to pay for services and other fees. You will be
          provided with these costs prior to beginning therapy, and should
          confirm with your insurance if part or all of these fees may be
          covered. You should also know about the following:
        </p>
        <ul>
          <li>
            <strong>No-Show and Late Cancellation Fees</strong>
          </li>
          <ul>
            <li>
              If you are unable to attend therapy, you must contact your
              Provider before your session. Otherwise, you may subject to fees
              outlined in your fee agreement. Insurance does not cover these
              fees.
            </li>
          </ul>
          <li>
            <strong>Balance Accrual</strong>
          </li>
          <ul>
            <li>
              Full payment is due at the time of your session. If you are unable
              to pay, tell your Provider. Your Provider may offer payment plans
              or a sliding scale. If not, your Provider may refer you to other
              low- or no-cost services. Any balance due will continue to be due
              until paid in full. If necessary, your balance may be sent to a
              collections service.
            </li>
          </ul>
          <li>
            <strong>Administrative Fees</strong>
          </li>
          <ul>
            <li>
              Your Provider may charge administrative fees for writing a letter
              or report at your request; consulting with another healthcare
              provider or other professional outside of normal case management
              practices; or for preparation, travel, and attendance at a court
              appearance. These fees are listed in the fee agreement. Payment is
              due in advance.
            </li>
          </ul>
          <li>
            <strong>Insurance Benefits</strong>
          </li>
          <ul>
            <li>
              Before starting therapy, you should confirm with your insurance
              company if:
            </li>
            <ul>
              <li>Your benefits cover the type of therapy you will receive;</li>
              <li>Your benefits cover in-person and telehealth sessions;</li>
              <li>
                You may be responsible for any portion of the payment, and
              </li>
              <li>Your Provider is in-network or out-of-network.</li>
            </ul>
          </ul>
          <li>
            <strong>Sharing Information with Insurance Companies</strong>
          </li>
          <ul>
            <li>
              If you choose to use insurance benefits to pay for services, you
              will be required to share personal information with your insurance
              company. Insurance companies keep personal information
              confidential unless they must share to act on your behalf, comply
              with federal or state law, or complete administrative work.
            </li>
          </ul>
          <li>
            <strong>Covered and Non-Covered Services</strong>
          </li>
          <ul>
            <li>
              When your Provider is in-network, they have a contract with your
              insurance company. Your insurance plan may cover all or part of
              the cost of therapy. You are responsible for any part of this cost
              not covered by insurance, such as deductibles, copays, or
              coinsurance. You may also be responsible for any services not
              covered by your insurance.
            </li>
            <li>
              When your Provider is out-of-network, they do not have a contract
              with your insurance company. You can still choose to see your
              Provider; however, all fees will be due at the time of your
              session to your Provider. Your Provider will tell you if they can
              help you file for reimbursement from your insurance company. If
              your insurance company decides that they will not reimburse you,
              you are still responsible for the full amount.
            </li>
          </ul>
          <li>
            <strong>Payment Methods</strong>
          </li>
          <ul>
            <li>
              The practice requires that you keep a valid credit or debit card
              on file. This card will be charged for the amount due at the time
              of service and for any fees you may accrue unless other
              arrangements have been made with the practice ahead of time. It is
              your responsibility to keep this information up to date, including
              providing new information if the card information changes or the
              account has insufficient funds to cover these charges.
            </li>
          </ul>
        </ul>

        <strong>COMPLAINTS</strong>
        <p>
          If you feel your Provider has engaged in improper or unethical
          behavior, you can talk to them, or you may contact the licensing board
          that issued your Provider's license, your insurance company (if
          applicable), or the US Department of Health and Human Services.
        </p>
      </Box>

      {/* Acknowledgment & Signature Verification Section */}
      <Box>
        <Text variant="body1" fontWeight="500" mb={0.5}>
          Acknowledgment
        </Text>
        <Text variant="body2" color="textSecondary" mb={2}>
          My signature on this document represents that I have received the
          Consent for Services form and that I understand and agree to the
          information therein. Further, I consent to use an electronic signature
          to acknowledge this agreement.
        </Text>

        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Input
              label="Signed By:"
              textFieldProps={{
                name: "consentForServices.signedByName",
                value: values?.consentForServices?.signedByName || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Enter Your Legal Name",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid size={12}>
            {viewOnly || values?.consentForServices?.sign ? (
              <ErrorHelper label="Signature">
                <Box
                  style={{
                    position: "relative",
                    borderWidth: 1,
                    borderRadius: 5,
                    borderStyle: "solid",
                    width: "368px",
                    height: "100%",
                  }}
                >
                  <img
                    src={values?.consentForServices?.sign}
                    alt="Consent for Services signature"
                    style={{ height: "150px", width: "360px" }}
                  />

                  {!viewOnly && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        setK((prev) => !prev);
                        signPad?.clear();
                        setFieldValue("consentForServices.sign", undefined);
                      }}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -20,
                        height: "10px",
                      }}
                    >
                      <HighlightOffIcon color="error" fontSize="medium" />
                    </IconButton>
                  )}
                </Box>
              </ErrorHelper>
            ) : (
              <Box
                onMouseLeave={() => {
                  if (!isSignEmpty() && signPad?.toDataURL()) {
                    setFieldValue(
                      "consentForServices.sign",
                      signPad?.toDataURL()
                    );
                  }
                }}
                sx={{
                  width: "368px",
                  position: "relative",
                }}
              >
                <SignatureInput
                  label="Signature"
                  canvasRef={canvasRef}
                  w="368px"
                  h="100px"
                />

                <Button
                  variant="contained"
                  sx={{
                    position: "absolute",
                    bottom: 10,
                    left: "300px",
                    height: "24px",
                  }}
                  onClick={() => {
                    setK((prev) => !prev);
                    signPad?.clear();
                  }}
                >
                  clear
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ConsentForServicesForm;
